import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

function SalarySpecificationForm({ open, onClose, salarySpecificationId, companyId, onSaved, onSaveAndAddAnother }) {
    const { register, handleSubmit, formState: { errors }, reset, control } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingSpecification, setLoadingSpecification] = useState(false);

    // Load employees for the company
    const { data: employeesData } = useQuery(
        ['employees', companyId],
        () => {
            if (!companyId) return { results: [], count: 0 };
            return JobHttpService.loadEmployees({ company: companyId });
        },
        {
            enabled: !!companyId,
            keepPreviousData: true
        }
    );

    const employees = employeesData?.results || employeesData || [];

    useEffect(() => {
        if (open) {
            if (salarySpecificationId) {
                loadSalarySpecification();
            } else {
                reset({
                    employee: '',
                    salary_period_start: null,
                    salary_period_end: null,
                    payment_date: null
                });
                setError('');
            }
        }
    }, [open, salarySpecificationId, reset]);

    const loadSalarySpecification = async () => {
        if (!salarySpecificationId) return;
        
        setLoadingSpecification(true);
        setError('');
        try {
            const specification = await JobHttpService.loadSalarySpecification(salarySpecificationId);
            reset({
                employee: specification.employee?.id || '',
                salary_period_start: specification.salary_period_start ? dayjs(specification.salary_period_start) : null,
                salary_period_end: specification.salary_period_end ? dayjs(specification.salary_period_end) : null,
                payment_date: specification.payment_date ? dayjs(specification.payment_date) : null
            });
        } catch (err) {
            console.error('Error loading salary specification:', err);
            setError(err?.message || 'Failed to load salary specification');
        } finally {
            setLoadingSpecification(false);
        }
    };

    const onSubmit = async (data, saveAndAddAnother = false) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                employee: data.employee,
                company: companyId,
                salary_period_start: data.salary_period_start ? dayjs(data.salary_period_start).format('YYYY-MM-DD') : null,
                salary_period_end: data.salary_period_end ? dayjs(data.salary_period_end).format('YYYY-MM-DD') : null,
                payment_date: data.payment_date ? dayjs(data.payment_date).format('YYYY-MM-DD') : null
            };

            let response;
            if (salarySpecificationId) {
                response = await JobHttpService.updateSalarySpecification(salarySpecificationId, submitData);
            } else {
                response = await JobHttpService.createSalarySpecification(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                
                if (saveAndAddAnother) {
                    // Reset form for new entry
                    reset({
                        employee: '',
                        salary_period_start: null,
                        salary_period_end: null,
                        payment_date: null
                    });
                    setError('');
                    // Keep modal open for another entry
                    if (onSaveAndAddAnother) {
                        onSaveAndAddAnother();
                    }
                } else {
                    onClose();
                }
            } else {
                setError(response?.detail || response?.error || 'Failed to save salary specification');
            }
        } catch (err) {
            console.error('Error saving salary specification:', err);
            setError(err?.message || 'Failed to save salary specification. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <form onSubmit={handleSubmit((data) => onSubmit(data, false))}>
                <DialogTitle>
                    Salary Specification
                </DialogTitle>
                <DialogContent>
                    {loadingSpecification && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingSpecification && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Employee</InputLabel>
                                        <Select
                                            label="Employee"
                                            {...register("employee", {
                                                required: "Employee is required"
                                            })}
                                            error={Boolean(errors.employee)}
                                        >
                                            {employees.map((employee) => (
                                                <MenuItem key={employee.id} value={employee.id}>
                                                    {employee.first_name} {employee.last_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {errors.employee && (
                                        <Alert severity="error" sx={{ mt: 0.5 }}>
                                            {errors.employee.message}
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="salary_period_start"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Salary period start"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="salary_period_end"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Salary period end"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="payment_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Payment date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || loadingSpecification}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                    {!salarySpecificationId && (
                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={loading || loadingSpecification}
                            onClick={handleSubmit((data) => onSubmit(data, true))}
                        >
                            Save and add another
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        fullWidth
                        color="inherit"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default SalarySpecificationForm;
